import React, { createContext, useCallback, useContext } from 'react';

import {
  useEditorRef,
  useEditorSelector,
  useElement,
  useFocused,
  useSelectionVersion,
} from 'platejs/react';
import { makeStyles } from 'tss-react/mui';

import { EmptyParagraphToolbar } from './AlineMarkdownEditorToolbar';
import { getEmptyParagraphActionsBlock, getIsEditorBlank } from './utils/empty-paragraph';

/** The width of the actions (181px) plus a gap, kept free at the end of the line hosting them. */
export const EMPTY_PARAGRAPH_ACTIONS_WIDTH = 188;

const EmptyParagraphActionsContext = createContext(false);
export const EmptyParagraphActionsProvider = EmptyParagraphActionsContext.Provider;

/** Whether this editor shows empty-paragraph actions at all: false when it is not editable. */
export function useEmptyParagraphActionsEnabled() {
  return useContext(EmptyParagraphActionsContext);
}

const useStyles = makeStyles()({
  /**
   * Centered on the first text line, so the buttons line up with the caret and placeholder.
   * Out of flow, so the line hosting them keeps its height.
   */
  actions: {
    display: 'inline-flex',
    flexShrink: 0,
    position: 'absolute',
    right: 0,
    top: '0.5lh',
    transform: 'translateY(-50%)',
    verticalAlign: 'middle',
  },
});

export function useShowEmptyParagraphActions(): boolean {
  const focused = useFocused();
  const enabled = useEmptyParagraphActionsEnabled();
  const element = useElement();
  const selectionVersion = useSelectionVersion();
  const isActionsHost = useEditorSelector(
    editor =>
      focused
        ? getEmptyParagraphActionsBlock(editor)?.[0] === element
        : editor.children[0] === element && getIsEditorBlank(editor),
    [element, focused, selectionVersion]
  );
  return enabled && isActionsHost;
}

/** Insert actions on the blank line the caret is on, shown while the editor has focus. */
export const EmptyParagraphActions = React.memo(function EmptyParagraphActions() {
  const { classes } = useStyles();
  const focused = useFocused();
  const editor = useEditorRef();
  const element = useElement();
  const keepCaret = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (!editor.selection) {
        const path = editor.api.findPath(element);
        if (!path) return;
        editor.tf.select(editor.api.start(path));
        editor.tf.focus();
      }
    },
    [editor, element]
  );
  if (!focused) return null;

  return (
    <span className={classes.actions} contentEditable={false} onMouseDownCapture={keepCaret}>
      <EmptyParagraphToolbar />
    </span>
  );
});
